import { useCallback, useMemo, useState } from 'react';
import { ExpandedComponent, NavigationSegment, findComponentAtPath, isCompositeComponent } from './types';

export type ViewMode = 'root' | 'component-list' | 'component-detail';

interface CompositeNavigationOptions {
  skipSingleItemRoot?: boolean;
}

export const useCompositeNavigation = (rootComponents: ExpandedComponent[], options?: CompositeNavigationOptions) => {
  const skippedRoot = Boolean(options?.skipSingleItemRoot && rootComponents.length === 1);

  const [path, setPath] = useState<NavigationSegment[]>(() => {
    if (!skippedRoot) {
      return [];
    }
    const first = rootComponents[0];
    if (!first) {
      return [];
    }
    return [{ instanceId: first.instanceId, itemVariantId: first.itemVariantId, name: first.name }];
  });

  const push = useCallback((segment: NavigationSegment) => {
    setPath((prev) => [...prev, segment]);
  }, []);

  const pop = useCallback(() => {
    setPath((prev) => {
      if (skippedRoot && prev.length <= 1) {
        return prev;
      }
      return prev.slice(0, -1);
    });
  }, [skippedRoot]);

  const reset = useCallback(() => {
    setPath([]);
  }, []);

  /** The component at the tip of the path (undefined at root) */
  const currentComponent = useMemo(() => findComponentAtPath(rootComponents, path), [rootComponents, path]);

  const currentTitle = path.length > 0 ? path[path.length - 1]!.name : undefined;

  /**
   * View mode:
   * - root: at root level, showing root components
   * - component-list: drilled into a composite, showing its sub-components
   * - component-detail: drilled into a leaf, showing its settings
   */
  const viewMode: ViewMode = useMemo(() => {
    if (path.length === 0) {
      return 'root';
    }
    if (currentComponent && isCompositeComponent(currentComponent)) {
      return 'component-list';
    }
    return 'component-detail';
  }, [path.length, currentComponent]);

  /** Components to render as rows (only relevant for root and component-list modes) */
  const listComponents = useMemo(() => {
    if (viewMode === 'root') {
      return rootComponents;
    }
    if (viewMode === 'component-list' && currentComponent?.components) {
      return currentComponent.components;
    }
    return [];
  }, [viewMode, rootComponents, currentComponent]);

  const canGoBack = skippedRoot ? path.length > 1 : path.length > 0;

  return { path, push, pop, reset, currentComponent, currentTitle, viewMode, listComponents, canGoBack };
};
